import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../apiClient';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useShop } from '../context/ShopContext';
import { useNavigate } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';

/* ─── Design tokens ────────────────────────────────────────── */
const WINE   = '#7B1D4F';
const WINE2  = '#5E1540';
const WINE3  = '#3D0D26';
const GREEN  = '#10B981';
const AMBER  = '#F59E0B';
const BLUE   = '#3B82F6';
const RED    = '#EF4444';
const GRAY50 = '#F9FAFB';
const GRAY100= '#F3F4F6';
const GRAY200= '#E5E7EB';
const GRAY400= '#9CA3AF';
const GRAY600= '#4B5563';
const GRAY900= '#111827';

/* ─── helpers ────────────────────────────────────────────────── */
const sumField  = (arr, key) => arr.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);
const totalCalc = (item) => (item.openingStock || 0) + (item.rece || 0) - (item.trans || 0);
const fmtINR    = (v) => `₹${Math.round(v ?? 0).toLocaleString('en-IN')}`;
const pad2      = (n) => String(n).padStart(2, '0');
const todayISO  = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; };

/* ─── KPI Card ───────────────────────────────────────────────── */
const KpiCard = ({ label, value, icon, accent, sub }) => (
  <div style={{
    flex: '1 1 150px', background: '#fff', borderRadius: 12,
    padding: '16px 18px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    borderTop: `3px solid ${accent}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: accent + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, flexShrink: 0,
      }}>{icon}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.6, lineHeight: 1.2 }}>
        {label}
        {sub && <div style={{ color: GRAY400, fontWeight: 500, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
    <div style={{ fontSize: 22, fontWeight: 900, color: GRAY900, letterSpacing: -0.5 }}>{value}</div>
  </div>
);

/* ─── Field wrapper ──────────────────────────────────────────── */
const FilterField = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: GRAY600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  padding: '8px 11px', borderRadius: 8, border: `1.5px solid ${GRAY200}`,
  fontSize: 13, outline: 'none', background: '#fff', color: GRAY900,
  minWidth: 0,
};

/* ─── Stock level badge ──────────────────────────────────────── */
const StockBadge = ({ qty }) => {
  const level = qty === 0 ? 'out' : qty <= 5 ? 'low' : qty <= 20 ? 'ok' : 'good';
  const cfg = {
    out:  { bg: '#FEF2F2', color: RED,   label: 'OUT' },
    low:  { bg: '#FFF7ED', color: AMBER, label: qty },
    ok:   { bg: '#F0FDF4', color: GREEN, label: qty },
    good: { bg: '#F0FDF4', color: GREEN, label: qty },
  }[level];
  return (
    <span style={{
      display: 'inline-block', minWidth: 38, textAlign: 'center',
      padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: cfg.bg, color: cfg.color,
    }}>{cfg.label}</span>
  );
};

/* ─── Empty state ────────────────────────────────────────────── */
const Empty = ({ message = 'No data found' }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px', color: GRAY400 }}>
    <div style={{ fontSize: 44, marginBottom: 10 }}>📦</div>
    <div style={{ fontWeight: 700, fontSize: 15, color: GRAY600, marginBottom: 4 }}>{message}</div>
    <div style={{ fontSize: 12 }}>Select a date range and click <strong>Get Report</strong></div>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
const StockManagementReport = () => {
  const navigate = useNavigate();
  const { userId } = useShop();

  const [shopId,  setShopId]  = useState(null);
  const [stockData, setStockData] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [fromDate,  setFromDate]  = useState(todayISO);
  const [toDate,    setToDate]    = useState(todayISO);
  const [language,  setLanguage]  = useState('English');
  const [search,    setSearch]    = useState('');
  const [perPage,   setPerPage]   = useState(20);
  const [page,      setPage]      = useState(1);
  const [sortCol,   setSortCol]   = useState(null);
  const [sortAsc,   setSortAsc]   = useState(true);

  /* auth guard */
  useEffect(() => {
    const sid = localStorage.getItem('shopId');
    const tok = localStorage.getItem('tokenString');
    if (!tok) { toast.error('Please log in first!'); navigate('/Signin'); return; }
    setShopId(sid);
  }, [navigate]);

  /* fetch */
  const fetchData = async () => {
    if (!shopId) return;
    setLoading(true);
    setStockData(null);
    try {
      const res = await apiClient.post('/Liquor/GetProductSummary', {
        shopId,
        fromDate: new Date(fromDate).toISOString(),
        toDate:   new Date(toDate).toISOString(),
        typeId:   language === 'English' ? 1 : 2,
      });
      setStockData(res.data?.data || []);
      setPage(1);
    } catch {
      toast.error('Failed to fetch stock data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) { toast.error('Please select both dates.'); return; }
    fetchData();
  };

  /* sort */
  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(p => !p);
    else { setSortCol(col); setSortAsc(true); }
    setPage(1);
  };

  /* derived data */
  const filtered = useMemo(() => {
    if (!stockData) return [];
    const q = search.trim().toLowerCase();
    let rows = q
      ? stockData.filter(r =>
          (r.pName   || '').toLowerCase().includes(q) ||
          (r.packing || '').toLowerCase().includes(q)
        )
      : [...stockData];
    if (sortCol) {
      rows.sort((a, b) => {
        const va = a[sortCol] ?? 0;
        const vb = b[sortCol] ?? 0;
        return sortAsc
          ? (typeof va === 'string' ? va.localeCompare(vb) : va - vb)
          : (typeof va === 'string' ? vb.localeCompare(va) : vb - va);
      });
    }
    return rows;
  }, [stockData, search, sortCol, sortAsc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage   = Math.min(page, totalPages);

  const paginated = useMemo(() =>
    filtered
      .slice((safePage - 1) * perPage, safePage * perPage)
      .map((item, i) => ({
        ...item,
        serial:    (safePage - 1) * perPage + i + 1,
        totalQty:  totalCalc(item),
        amount:    (item.sales || 0) * (item.mrp || 0),
      })),
    [filtered, safePage, perPage]
  );

  /* KPI totals */
  const kpi = useMemo(() => ({
    products:  filtered.length,
    received:  sumField(filtered, 'rece'),
    sales:     sumField(filtered, 'sales'),
    closing:   sumField(filtered, 'closingStock'),
    amount:    filtered.reduce((s, r) => s + (r.sales || 0) * (r.mrp || 0), 0),
  }), [filtered]);

  /* footer totals */
  const footer = useMemo(() => filtered.length === 0 ? null : {
    openingStock: sumField(filtered, 'openingStock'),
    rece:         sumField(filtered, 'rece'),
    trans:        sumField(filtered, 'trans'),
    totalQty:     filtered.reduce((s, r) => s + totalCalc(r), 0),
    closingStock: sumField(filtered, 'closingStock'),
    sales:        sumField(filtered, 'sales'),
    amount:       filtered.reduce((s, r) => s + (r.sales || 0) * (r.mrp || 0), 0),
  }, [filtered]);

  /* export */
  const exportToExcel = () => {
    if (!filtered.length) { toast.error('No data to export!'); return; }
    const rows = filtered.map((item, i) => ({
      'S.No.':         i + 1,
      'Product Name':  item.pName   || 'N/A',
      'Variant':       item.packing || '',
      'Opening Stock': item.openingStock || 0,
      'Received':      item.rece    || 0,
      'Transfer':      item.trans   || 0,
      'Total Qty':     totalCalc(item),
      'Closing Stock': item.closingStock || 0,
      'Sales':         item.sales   || 0,
      'Rate (MRP)':    item.mrp     || 0,
      'Amount (₹)':   (item.sales || 0) * (item.mrp || 0),
    }));
    rows.push({
      'S.No.': 'TOTAL', 'Product Name': '', 'Variant': '',
      'Opening Stock': kpi.received,
      'Received':      kpi.received,
      'Transfer':      sumField(filtered, 'trans'),
      'Total Qty':     filtered.reduce((s, r) => s + totalCalc(r), 0),
      'Closing Stock': kpi.closing,
      'Sales':         kpi.sales,
      'Rate (MRP)':    '',
      'Amount (₹)':   kpi.amount,
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })],
               { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Stock_Report_${language}_${fromDate}_to_${toDate}.xlsx`
    );
    toast.success('Exported successfully!');
  };

  /* ── column config ─────────────────────────────────────────── */
  const COLS = [
    { key: 'serial',       label: 'S.No.',        w: 44,  align: 'center' },
    { key: 'pName',        label: 'Product Name', w: null, sortable: true },
    { key: 'packing',      label: 'Packing',      w: 90,  sortable: true },
    { key: 'openingStock', label: 'Opening',   w: 72,  align: 'right', sortable: true },
    { key: 'rece',         label: 'Received',  w: 80,  align: 'right', sortable: true,
      render: (v) => <span style={{ color: GREEN, fontWeight: 700 }}>{v || 0}</span> },
    { key: 'trans',        label: 'Transfer',  w: 76,  align: 'right', sortable: true,
      render: (v) => <span style={{ color: AMBER, fontWeight: 700 }}>{v || 0}</span> },
    { key: 'totalQty',     label: 'Total',     w: 68,  align: 'right' },
    { key: 'closingStock', label: 'Closing',   w: 72,  align: 'right',
      render: (v) => <StockBadge qty={v || 0} /> },
    { key: 'sales',        label: 'Sales',     w: 68,  align: 'right', sortable: true,
      render: (v) => <span style={{ fontWeight: 700, color: WINE }}>{v || 0}</span> },
    { key: 'mrp',          label: 'Rate',      w: 70,  align: 'right',
      render: (v) => <span style={{ color: GRAY600 }}>₹{v || 0}</span> },
    { key: 'amount',       label: 'Amount',    w: 100, align: 'right', sortable: true,
      render: (v) => <span style={{ fontWeight: 800, color: WINE }}>{fmtINR(v)}</span> },
  ];

  const sortIcon = (col) => {
    if (sortCol !== col) return <span style={{ opacity: 0.25, marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4, color: '#fff' }}>{sortAsc ? '↑' : '↓'}</span>;
  };

  const showData = stockData !== null && !loading;

  return (
    <div style={{ background: GRAY50, minHeight: '100vh', padding: '20px 20px 60px' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Page header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${WINE3} 0%, ${WINE} 100%)`,
        borderRadius: 14, padding: '20px 24px', marginBottom: 20,
        boxShadow: '0 4px 20px rgba(61,13,38,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 22 }}>📦</span>
            <h2 style={{ margin: 0, fontWeight: 900, fontSize: 20, color: '#fff', letterSpacing: -0.3 }}>
              Inventory Report
            </h2>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.60)', marginLeft: 32 }}>
            Stock movement &nbsp;·&nbsp; {language} &nbsp;·&nbsp;
            <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
              {fromDate} → {toDate}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
          {language === 'English' ? '🍾 English' : '🍶 Desi'}
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '16px 18px', marginBottom: 18,
        boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>

            <FilterField label="From Date">
              <input type="date" style={inputStyle} value={fromDate}
                onChange={e => setFromDate(e.target.value)} />
            </FilterField>

            <FilterField label="To Date">
              <input type="date" style={inputStyle} value={toDate}
                onChange={e => setToDate(e.target.value)} />
            </FilterField>

            <FilterField label="Type">
              <select style={inputStyle} value={language}
                onChange={e => setLanguage(e.target.value)}>
                <option value="English">English</option>
                <option value="Desi">Desi</option>
              </select>
            </FilterField>

            <FilterField label="Per Page">
              <select style={inputStyle} value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
                {[10, 20, 50, 100].map(n => <option key={n}>{n}</option>)}
              </select>
            </FilterField>

            <FilterField label="Search">
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: GRAY400, fontSize: 13 }}>🔍</span>
                <input type="text" style={{ ...inputStyle, paddingLeft: 28, minWidth: 180 }}
                  placeholder="Product or variant…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </FilterField>

            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'flex-end' }}>
              <button type="submit" disabled={loading}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: loading ? GRAY200 : `linear-gradient(135deg,${WINE2},${WINE})`,
                  color: loading ? GRAY400 : '#fff', fontWeight: 700, fontSize: 13,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {loading
                  ? <><FaSpinner style={{ animation: 'spin 0.8s linear infinite' }} /> Loading…</>
                  : '+ Get Data'
                }
              </button>
            </div>

          </div>
        </form>
      </div>

      {/* ── KPI cards ── */}
      {showData && stockData.length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <KpiCard label="Products"       value={kpi.products}         icon="📦" accent={BLUE}  />
          <KpiCard label="Total Received" value={kpi.received}         icon="⬇️" accent={GREEN} />
          <KpiCard label="Total Sales"    value={kpi.sales}            icon="🛒" accent={WINE}  />
          <KpiCard label="Closing Stock"  value={kpi.closing}          icon="📋" accent={AMBER} />
          <KpiCard label="Sales Amount"   value={fmtINR(kpi.amount)}   icon="💰" accent={WINE2} />
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: GRAY400 }}>
          <FaSpinner style={{ fontSize: 28, animation: 'spin 1s linear infinite' }} />
          <div style={{ marginTop: 12, fontSize: 13 }}>Loading inventory…</div>
        </div>
      )}

      {/* ── No data yet ── */}
      {!loading && stockData === null && <Empty message="Select dates and click Get Report" />}

      {/* ── Empty results ── */}
      {!loading && stockData !== null && filtered.length === 0 && (
        <Empty message={search ? 'No products match your search' : 'No stock data for this period'} />
      )}

      {/* ── Data table (desktop) ── */}
      {showData && filtered.length > 0 && (
        <>
          <div className="sr-table-wrap" style={{
            background: '#fff', borderRadius: 14,
            boxShadow: '0 1px 10px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 14,
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>

                {/* thead */}
                <thead>
                  <tr style={{ background: `linear-gradient(135deg, ${WINE3}, ${WINE})` }}>
                    {COLS.map(col => (
                      <th key={col.key}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        style={{
                          padding: '11px 10px',
                          textAlign: col.align || 'left',
                          color: '#fff', fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                          whiteSpace: 'nowrap',
                          width: col.w ? col.w : undefined,
                          cursor: col.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255,255,255,0.10)',
                        }}>
                        {col.label}{col.sortable && sortIcon(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* tbody */}
                <tbody>
                  {paginated.map((row, ri) => (
                    <tr key={ri}
                      style={{
                        background: ri % 2 === 0 ? '#fff' : GRAY50,
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#FDF4F8'}
                      onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#fff' : GRAY50}
                    >
                      {COLS.map(col => (
                        <td key={col.key}
                          style={{
                            padding: '9px 10px',
                            textAlign: col.align || 'left',
                            color: GRAY900,
                            borderBottom: `1px solid ${GRAY100}`,
                            whiteSpace: col.key === 'pName' ? 'normal' : 'nowrap',
                            fontWeight: col.key === 'pName' ? 600 : 400,
                          }}>
                          {col.render
                            ? col.render(row[col.key], row)
                            : row[col.key] ?? '—'
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>

                {/* tfoot */}
                {footer && (
                  <tfoot>
                    <tr style={{ background: `${WINE}0d`, fontWeight: 800 }}>
                      <td colSpan={3} style={{ padding: '10px 10px', borderTop: `2px solid ${WINE}30`, color: WINE, fontWeight: 800, fontSize: 12 }}>
                        GRAND TOTAL ({filtered.length} items)
                      </td>
                      {[
                        footer.openingStock,
                        footer.rece,
                        footer.trans,
                        footer.totalQty,
                        footer.closingStock,
                        footer.sales,
                      ].map((v, i) => (
                        <td key={i} style={{
                          padding: '10px 10px', textAlign: 'right',
                          borderTop: `2px solid ${WINE}30`,
                          color: WINE, fontWeight: 800,
                        }}>{v}</td>
                      ))}
                      <td style={{ padding: '10px 10px', textAlign: 'right', borderTop: `2px solid ${WINE}30`, color: WINE, fontWeight: 800 }}>—</td>
                      <td style={{ padding: '10px 10px', textAlign: 'right', borderTop: `2px solid ${WINE}30`, color: WINE, fontWeight: 800 }}>
                        {fmtINR(footer.amount)}
                      </td>
                    </tr>
                  </tfoot>
                )}

              </table>
            </div>
          </div>

          {/* ── Pagination ── */}
          <div className="sr-pagination" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10, padding: '4px 2px',
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <PagBtn onClick={() => setPage(p => p - 1)} disabled={safePage === 1} label="Previous" wide />
              <span style={{ fontSize: 13, color: GRAY600, fontWeight: 500, whiteSpace: 'nowrap' }}>
                Page <strong style={{ color: GRAY900 }}>{safePage}</strong> of <strong style={{ color: GRAY900 }}>{totalPages}</strong>
              </span>
              <PagBtn onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages} label="Next" wide />
            </div>
            <button type="button" onClick={exportToExcel}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg,#059669,#10B981)',
                color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>
              Export to Excel
            </button>
          </div>

          {/* ── Mobile cards ── */}
          <div style={{ display: 'none' }} className="sm-cards">
            {filtered.map((item, i) => {
              const computed = { ...item, totalQty: totalCalc(item), amount: (item.sales||0)*(item.mrp||0) };
              return <MobileCard key={i} row={computed} index={i} />;
            })}
            {/* Mobile totals */}
            {footer && (
              <div style={{
                background: `linear-gradient(135deg,${WINE2},${WINE})`,
                borderRadius: 12, padding: '16px', color: '#fff', marginTop: 10,
              }}>
                <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>Grand Total</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                  {[
                    ['Received',  footer.rece],
                    ['Sales',     footer.sales],
                    ['Closing',   footer.closingStock],
                    ['Opening',   footer.openingStock],
                    ['Transfer',  footer.trans],
                    ['Total Qty', footer.totalQty],
                  ].map(([l, v]) => (
                    <div key={l} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 9, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{l}</div>
                      <div style={{ fontSize: 17, fontWeight: 900 }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, opacity: 0.75 }}>Total Amount</span>
                  <span style={{ fontSize: 20, fontWeight: 900 }}>{fmtINR(footer.amount)}</span>
                </div>
              </div>
            )}
          </div>

        </>
      )}

      <style>{`
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        @media (max-width: 1363px) {
          .sr-table-wrap { display: none !important; }
          .sm-cards { display: block !important; }
        }
      `}</style>
    </div>
  );
};

/* ─── Pagination button ────────────────────────────────────── */
const PagBtn = ({ onClick, disabled, label, wide }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      height: 32, borderRadius: 6, border: `1.5px solid ${GRAY200}`,
      background: disabled ? GRAY50 : '#fff', color: disabled ? GRAY400 : GRAY600,
      fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer',
      padding: wide ? '0 14px' : '0',
      minWidth: wide ? undefined : 32,
    }}>{label}</button>
);

/* ─── Mobile card ───────────────────────────────────────────── */
const MobileCard = ({ row }) => (
  <div style={{ background: '#fff', borderRadius: 12, marginBottom: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
    {/* Header */}
    <div style={{
      background: `linear-gradient(135deg, ${WINE3}, ${WINE})`,
      padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#fff', flex: 1, lineHeight: 1.3 }}>{row.pName || 'N/A'}</div>
      <span style={{ background: '#fff', color: WINE, fontWeight: 700, fontSize: 12, borderRadius: 20, padding: '3px 10px', flexShrink: 0 }}>{row.packing}</span>
    </div>

    {/* Stock Movement */}
    <div style={{ padding: '10px 14px 0' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>Stock Movement</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginBottom: 12 }}>
        {[
          { l: 'Opening',  v: row.openingStock || 0, c: GRAY900 },
          { l: 'Received', v: row.rece || 0,         c: GREEN },
          { l: 'Transfer', v: row.trans || 0,        c: AMBER },
          { l: 'Total',    v: row.totalQty,          c: BLUE },
          { l: 'Closing',  v: row.closingStock || 0, c: (row.closingStock || 0) === 0 ? RED : GREEN },
          { l: 'Sales',    v: row.sales || 0,        c: WINE },
        ].map(({ l, v, c }) => (
          <div key={l} style={{ textAlign: 'center', background: GRAY50, borderRadius: 8, padding: '7px 4px' }}>
            <div style={{ fontSize: 8, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{l}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Financials */}
    <div style={{ padding: '0 14px 12px' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>Financials</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={{ background: GRAY50, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Rate (MRP)</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: GRAY900 }}>₹{row.mrp || 0}</div>
        </div>
        <div style={{ background: GRAY50, borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: GRAY400, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Sales Value</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: WINE }}>{fmtINR(row.amount)}</div>
        </div>
      </div>
    </div>
  </div>
);

export default StockManagementReport;
